import { ReactNode, useEffect } from "react"
import { Navigate, useLocation } from "react-router-dom"

import { USER_ACCOUNT_LOADING_STATUS } from "../../ClientAuthorization/Default/common/constant"

import { BackButtonWithHistory, HeaderLogo, HeaderMenu, HeaderRoot } from "components/Header"
import { Spinner } from "components/Spinner"
import { useClientContext } from "modules/ClientAuthorization/common/ClientContext"
import { useGetBookingHash } from "modules/Call/routing"
import { resolveRedirect } from "modules/Call/ClientRedirects"
import { useGetClientCall } from "modules/Call/queries"
import { CALL_PAGE, OWNER } from "modules/Call/common/constants"
import { LobbyVideo } from "modules/Call/components/LobbyVideo"
import { LobbyInfoForClient } from "modules/Call/components/LobbyInfoForClient"
import { LobbyPageLayout } from "modules/Call/components/LobbyPageLayout"
import { useInputDevicesContext } from "modules/Call/InputDevices"
import { isServerOrConnectionError } from "utils/helpers/isServerOrConnectionError"
import { isVerified } from "modules/ClientAuthorization/common/utils"

interface Props {
    renderAvatar: (isHidden: boolean) => ReactNode
}

export function ClientLobbyPage({ renderAvatar }: Props) {
    const [{ loading, userAccount }] = useClientContext()
    const [{ cameraEnabled }] = useInputDevicesContext()
    const location = useLocation()
    const bookingHash = useGetBookingHash(true)

    const { data, isError, error, refetch } = useGetClientCall(bookingHash, CALL_PAGE.LOBBY, true)

    useEffect(() => {
        void refetch()
    }, [location])

    if (isError && !isServerOrConnectionError(error)) return <Navigate to="/my/alter" replace /> // Заменено Redirect на Navigate

    if (loading !== USER_ACCOUNT_LOADING_STATUS.READY || !data) return <Spinner />

    const userName = isVerified(userAccount) ? userAccount.voximplant_user_name : undefined
    if (!userName) throw new Error("Pass voximplant user name")

    const redirect = resolveRedirect(data.status, CALL_PAGE.LOBBY)
    if (redirect) return redirect({ bookingHash: bookingHash })

    return (
        <LobbyPageLayout
            header={
                <HeaderRoot>
                    <BackButtonWithHistory />
                    <HeaderLogo>alter</HeaderLogo>
                    <HeaderMenu />
                </HeaderRoot>
            }
            left={
                <LobbyVideo
                    avatar={renderAvatar(cameraEnabled)}
                    voximplantUserName={userName}
                    generateTokenApiUrl="/call/v2/client/generate-token"
                    owner={OWNER.Client}
                    bookingHash={bookingHash}
                />
            }
            right={
                <LobbyInfoForClient
                    sessionDatetime={data.booking.session_start_datetime}
                    sessionDuration={data.booking.session_duration}
                    psyName={data.booking.psychologist.display_name}
                    faceUrl={data.booking.psychologist.face_url}
                    titles={data.booking.psychologist.titles}
                    bookingHash={bookingHash}
                    callStatus={data.status}
                    companionLocation={data.companion_status}
                />
            }
        />
    )
}
